/**
 * Settings reducer — slice dédié à la page Paramètres / onglet Intégrations.
 * State shape (généralisé par type d'intégration) :
 * {
 *   integrations: {
 *     [type]: {
 *       enabled: bool,
 *       configured: bool,
 *       settings: {},              // ex. { baseUrl, paymentsSince } pour Prospect
 *       secretsMasked: {},         // ex. { customers: "abcd***", ... } — jamais la valeur brute
 *       updatedAt: string|null,
 *       updatedBy: string|null,
 *       sync: {...} | null,        // état de la dernière synchro renvoyé par le GET (running, lastStatus, categories, cursors...)
 *       outboundEnabled: bool|undefined, // état de l'environnement (envois autorisés) ; undefined sur un ancien domainModel
 *       simulation: bool,          // mode effectif du compte (!outboundEnabled || settings.simulation=="true")
 *       loading: bool,
 *       error: string|null,
 *       saving: bool,
 *       saveError: string|null,
 *       testing: bool,
 *       testResult: { status: string, categories: {...} } | null,
 *       testError: string|null,
 *       testErrorCode: number|null,   // ex. 503 = envoi désactivé sur cet environnement (bandeau info, pas erreur)
 *       syncing: bool,
 *       syncMessage: string|null,
 *       syncError: string|null,
 *       syncErrorCode: number|null,   // idem 503
 *       logs: [entrée du journal],    // journal des envois, du plus récent au plus ancien
 *       logsLoading: bool,
 *       logsError: string|null,
 *       logsNextBefore: string|null,  // curseur « Plus ancien » (null = plus rien d'ancien)
 *       logsForbidden: bool,          // 404 « No Access » (RBAC) ou 403 : section masquée
 *       logsRequestSeq: number,       // numéro de la dernière requête START : SUCCESS/FAILURE
 *                                     // d'une requête plus ancienne (rechargement vs « Plus ancien »
 *                                     // partis en course) sont ignorés pour ne pas créer de trous
 *     }
 *   },
 *   boxes: {                         // liste blanche des associations box/client envoyées
 *     [type]: {
 *       selected: [boxAssoId],       // associations cochées (sélection vide = rien n'est envoyé)
 *       synced: { boxAssoId: date }, // installations acceptées (201), y compris retirées depuis
 *       excluded: { boxAssoId: motif }, // box sélectionnées écartées à la dernière synchro réelle
 *       updatedAt: string|null,
 *       updatedBy: string|null,
 *       fetching: bool,
 *       fetched: bool,
 *       error: string|null,          // erreur du GET ou réponse sans liste (la page Home masque alors sa colonne)
 *       forbidden: bool,             // 404 « No Access » (RBAC) ou 403 (accès suspendu)
 *       saving: bool,
 *       saveError: string|null,      // erreur du PUT, distincte pour ne pas masquer la colonne Home
 *     }
 *   }
 * }
 */

const initialIntegration = {
    enabled: false,
    configured: false,
    settings: {},
    secretsMasked: {},
    updatedAt: null,
    updatedBy: null,
    sync: null,
    // État de l'environnement (envois autorisés) et mode effectif du compte — undefined tant que
    // non chargés ou sur un ancien domainModel qui ne les renvoie pas encore.
    outboundEnabled: undefined,
    simulation: false,
    loading: false,
    error: null,
    saving: false,
    saveError: null,
    testing: false,
    testResult: null,
    testError: null,
    testErrorCode: null,
    syncing: false,
    syncMessage: null,
    syncError: null,
    syncErrorCode: null,
    logs: [],
    logsLoading: false,
    logsError: null,
    logsNextBefore: null,
    logsForbidden: false,
    logsRequestSeq: 0,
};

const initialBoxes = {
    selected: [],
    synced: {},
    excluded: {},
    updatedAt: null,
    updatedBy: null,
    fetching: false,
    fetched: false,
    error: null,
    forbidden: false,
    saving: false,
    saveError: null,
};

const initialState = {
    integrations: {},
    boxes: {},
};

function integrationOf(state, type) {
    return state.integrations[type] || { ...initialIntegration };
}

function withIntegration(state, type, patch) {
    return {
        ...state,
        integrations: {
            ...state.integrations,
            [type]: {
                ...integrationOf(state, type),
                ...patch,
            },
        },
    };
}

function withBoxes(state, type, patch) {
    const boxes = state.boxes || {};
    return {
        ...state,
        boxes: {
            ...boxes,
            [type]: {
                ...(boxes[type] || initialBoxes),
                ...patch,
            },
        },
    };
}

export function settingsReducer(state = initialState, { type, payload }) {
    if (!payload || !payload.integrationType) {
        return state;
    }
    const integrationType = payload.integrationType;

    switch (type) {

        // --- GET ---
        case 'SETTINGS_GET_INTEGRATION_START':
            return withIntegration(state, integrationType, { loading: true, error: null });

        case 'SETTINGS_GET_INTEGRATION_SUCCESS':
            return withIntegration(state, integrationType, {
                loading: false,
                error: null,
                configured: !!payload.configured,
                enabled: !!payload.enabled,
                settings: payload.settings || {},
                secretsMasked: payload.secretsMasked || {},
                updatedAt: payload.updatedAt || null,
                updatedBy: payload.updatedBy || null,
                sync: payload.sync || null,
                outboundEnabled: payload.outboundEnabled,
                simulation: !!payload.simulation,
            });

        case 'SETTINGS_GET_INTEGRATION_ERROR':
            return withIntegration(state, integrationType, { loading: false, error: payload.error });

        // --- SAVE ---
        case 'SETTINGS_SAVE_INTEGRATION_START':
            return withIntegration(state, integrationType, { saving: true, saveError: null });

        case 'SETTINGS_SAVE_INTEGRATION_SUCCESS':
            return withIntegration(state, integrationType, {
                saving: false,
                saveError: null,
                enabled: !!payload.enabled,
                configured: !!payload.configured,
                settings: payload.settings || integrationOf(state, integrationType).settings,
                secretsMasked: payload.secretsMasked || integrationOf(state, integrationType).secretsMasked,
                outboundEnabled: payload.outboundEnabled !== undefined
                    ? payload.outboundEnabled
                    : integrationOf(state, integrationType).outboundEnabled,
                simulation: payload.simulation !== undefined
                    ? !!payload.simulation
                    : integrationOf(state, integrationType).simulation,
            });

        case 'SETTINGS_SAVE_INTEGRATION_ERROR':
            return withIntegration(state, integrationType, { saving: false, saveError: payload.error });

        // --- TEST ---
        case 'SETTINGS_TEST_INTEGRATION_START':
            return withIntegration(state, integrationType, { testing: true, testResult: null, testError: null, testErrorCode: null });

        case 'SETTINGS_TEST_INTEGRATION_SUCCESS':
            return withIntegration(state, integrationType, {
                testing: false,
                testResult: { status: payload.status, categories: payload.categories || {} },
                testError: null,
                testErrorCode: null,
            });

        case 'SETTINGS_TEST_INTEGRATION_ERROR':
            return withIntegration(state, integrationType, {
                testing: false,
                testResult: null,
                testError: payload.error,
                testErrorCode: payload.errorCode || null,
            });

        // --- SYNC ---
        case 'SETTINGS_SYNC_INTEGRATION_START':
            return withIntegration(state, integrationType, { syncing: true, syncMessage: null, syncError: null, syncErrorCode: null });

        case 'SETTINGS_SYNC_INTEGRATION_SUCCESS':
            return withIntegration(state, integrationType, {
                syncing: false,
                syncMessage: payload.message,
                syncError: null,
                syncErrorCode: null,
            });

        case 'SETTINGS_SYNC_INTEGRATION_ERROR':
            return withIntegration(state, integrationType, {
                syncing: false,
                syncMessage: null,
                syncError: payload.error,
                syncErrorCode: payload.errorCode || null,
            });

        // --- JOURNAL DES ENVOIS ---
        // logsRequestSeq départage un rechargement automatique (reloadKey, fin de synchro) parti
        // pendant un « Plus ancien » en cours : seule la requête START la plus récente peut faire
        // aboutir un SUCCESS/FAILURE, sinon la réponse arrivée en retard s'ajouterait avec un
        // curseur périmé (trous dans la liste).
        case 'SETTINGS_GET_INTEGRATION_LOGS_START':
            return withIntegration(state, integrationType, { logsLoading: true, logsError: null, logsRequestSeq: payload.seq });

        case 'SETTINGS_GET_INTEGRATION_LOGS_SUCCESS': {
            const integration = integrationOf(state, integrationType);
            if (payload.seq !== integration.logsRequestSeq) {
                return state;
            }
            return withIntegration(state, integrationType, {
                logsLoading: false,
                logsError: null,
                logsForbidden: false,
                logs: payload.append
                    ? [...(integration.logs || []), ...(payload.logs || [])]
                    : (payload.logs || []),
                logsNextBefore: payload.nextBefore || null,
            });
        }

        case 'SETTINGS_GET_INTEGRATION_LOGS_ERROR': {
            const integration = integrationOf(state, integrationType);
            if (payload.seq !== integration.logsRequestSeq) {
                return state;
            }
            return withIntegration(state, integrationType, {
                logsLoading: false,
                logsError: payload.error,
                logsForbidden: !!payload.forbidden,
            });
        }

        // --- BOX SÉLECTIONNÉES (GET) ---
        case 'SETTINGS_GET_INTEGRATION_BOXES_START':
            return withBoxes(state, integrationType, { fetching: true, error: null });

        case 'SETTINGS_GET_INTEGRATION_BOXES_SUCCESS':
            return withBoxes(state, integrationType, {
                fetching: false,
                fetched: true,
                error: null,
                forbidden: false,
                selected: payload.selected || [],
                synced: payload.synced || {},
                excluded: payload.excluded || {},
                updatedAt: payload.updatedAt || null,
                updatedBy: payload.updatedBy || null,
            });

        case 'SETTINGS_GET_INTEGRATION_BOXES_ERROR':
            return withBoxes(state, integrationType, {
                fetching: false,
                fetched: true,
                error: payload.error,
                forbidden: !!payload.forbidden,
            });

        // --- BOX SÉLECTIONNÉES (PUT) ---
        case 'SETTINGS_SAVE_INTEGRATION_BOXES_START':
            return withBoxes(state, integrationType, { saving: true, saveError: null });

        case 'SETTINGS_SAVE_INTEGRATION_BOXES_SUCCESS':
            return withBoxes(state, integrationType, {
                saving: false,
                saveError: null,
                fetched: true,
                error: null,
                forbidden: false,
                selected: payload.selected || [],
                synced: payload.synced || {},
                excluded: payload.excluded || {},
                updatedAt: payload.updatedAt || null,
                updatedBy: payload.updatedBy || null,
            });

        case 'SETTINGS_SAVE_INTEGRATION_BOXES_ERROR':
            return withBoxes(state, integrationType, { saving: false, saveError: payload.error });

        case 'SETTINGS_RESET_INTEGRATION_BOXES_SAVE_ERROR':
            return withBoxes(state, integrationType, { saveError: null });

        default:
            return state;
    }
}
